import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Building2, Users, Briefcase, Settings, LogOut, Menu, X, QrCode, Sparkles, Package, CreditCard, Palette, Bell, MessageSquare, FileText, HelpCircle, Cloud, Calculator, Timer, ChevronLeft, ChevronRight, Scan } from 'lucide-react';
import OnboardingHints from '../../components/OnboardingHints';
import LanguageSelectorWithPlan from '../../components/LanguageSelectorWithPlan';
import { companyAPI } from '../../utils/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

const ControlPanelLayout = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile menu
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load saved preference from localStorage
    const saved = localStorage.getItem('movemate_sidebar_collapsed');
    return saved === 'true';
  });
  const [subscription, setSubscription] = useState(null);
  
  // Save sidebar collapsed preference
  useEffect(() => {
    localStorage.setItem('movemate_sidebar_collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);
  
  // Fetch company subscription info for language feature check
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await companyAPI.getSubscription();
        // Map the response to the expected format for LanguageSelectorWithPlan
        const subData = response.data;
        setSubscription({
          plan: subData.plan_type, // 'white_label', 'professional', 'basic'
          status: subData.status, // 'trialing', 'active', 'canceled'
          trial_ends_at: subData.trial_end,
          days_left: subData.days_left_in_trial
        });
      } catch (error) {
        console.log('Could not fetch subscription:', error);
      }
    };
    fetchSubscription();
  }, []);
  
  // Map routes to page names for onboarding hints
  const getPageName = () => {
    const path = location.pathname;
    if (path === '/control-panel') return 'dashboard';
    if (path.includes('/users')) return 'users';
    if (path.includes('/jobs')) return 'jobs';
    if (path.includes('/qr-codes')) return 'qr-codes';
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/billing')) return 'billing';
    if (path.includes('/settings')) return 'settings';
    return '';
  };

  const handleLogout = () => {
    localStorage.removeItem('movemate_token');
    localStorage.removeItem('movemate_user');
    setUser(null);
    navigate('/login');
  };

  const menuItems = [
    { path: '/control-panel', icon: <Building2 size={20} />, label: t('nav.dashboard'), exact: true },
    { path: '/control-panel/users', icon: <Users size={20} />, label: t('settings.users') },
    { path: '/control-panel/jobs', icon: <Briefcase size={20} />, label: t('nav.jobs') },
    { path: '/control-panel/inventory', icon: <Package size={20} />, label: t('nav.inventory') },
    { path: '/control-panel/scan-console', icon: <Scan size={20} />, label: 'Scan Console' },
    { path: '/control-panel/qr-codes', icon: <QrCode size={20} />, label: t('nav.qr_codes') },
    { path: '/control-panel/estimate-settings', icon: <Calculator size={20} />, label: t('nav.estimates') },
    { path: '/control-panel/time-tracking', icon: <Timer size={20} />, label: t('nav.driver_tracking') },
    { path: '/control-panel/ai-credits', icon: <Sparkles size={20} />, label: t('nav.ai_credits') },
    { path: '/control-panel/storage', icon: <Cloud size={20} />, label: t('nav.storage') },
    { path: '/control-panel/messages', icon: <MessageSquare size={20} />, label: t('nav.messages') },
    { path: '/control-panel/team-chat', icon: <Users size={20} />, label: 'Team Chat' },
    { path: '/control-panel/notifications', icon: <Bell size={20} />, label: t('nav.notifications') },
    { path: '/control-panel/notification-templates', icon: <FileText size={20} />, label: t('notifications.templates') },
    { path: '/control-panel/billing', icon: <CreditCard size={20} />, label: t('nav.billing') },
    { path: '/control-panel/branding', icon: <Palette size={20} />, label: t('nav.branding') },
    { path: '/control-panel/support', icon: <HelpCircle size={20} />, label: t('nav.support') },
    { path: '/control-panel/settings', icon: <Settings size={20} />, label: t('nav.settings') },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';
  const mainMargin = sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64';

  return (
    <TooltipProvider delayDuration={100}>
      <div className="min-h-screen bg-gray-50 flex" data-testid="control-panel">
        {/* Sidebar */}
        <aside className={`bg-white border-r border-gray-200 ${sidebarWidth} fixed h-full z-30 transition-all duration-300 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          {/* Logo Section */}
          <div className="p-4 border-b flex-shrink-0">
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
              <img src="/logo.png" alt="MoveMate" className="h-10" />
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-xl font-bold gradient-text">MoveMate</h1>
                  <p className="text-xs text-gray-500">Control Panel</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
            {menuItems.map((item) => {
              const button = (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.icon}
                  {!sidebarCollapsed && <span className="font-medium text-sm">{item.label}</span>}
                </button>
              );

              // Wrap in tooltip when collapsed
              if (sidebarCollapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      {button}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-gray-900 text-white">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return button;
            })}
          </nav>

          {/* User Section */}
          <div className="p-3 border-t flex-shrink-0">
            {!sidebarCollapsed && (
              <div className="mb-3 px-2">
                <p className="text-sm font-semibold truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                <p className="text-xs text-gray-400">{user?.role}</p>
              </div>
            )}
            
            {sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-full"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-gray-900 text-white">
                  Logout
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            )}
          </div>

          {/* Collapse Toggle Button - Desktop only - Positioned at edge of sidebar */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex items-center justify-center absolute top-16 -right-3 w-6 h-6 bg-white hover:bg-gray-100 border border-gray-300 rounded-full shadow-md transition-colors z-50"
            data-testid="sidebar-collapse-toggle"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <ChevronRight size={14} className="text-gray-600" />
            ) : (
              <ChevronLeft size={14} className="text-gray-600" />
            )}
          </button>
        </aside>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-4 left-4 z-40 bg-white p-2 rounded-lg shadow-lg"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-20"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className={`flex-1 ${mainMargin} transition-all duration-300`}>
          {/* Top Bar with Language Selector */}
          <div className="bg-white border-b border-gray-200 px-8 py-3 flex justify-end items-center gap-4">
            <LanguageSelectorWithPlan 
              variant="flag-only" 
              subscription={subscription}
              isCustomer={false}
            />
          </div>
          <div className="p-8">
            <Outlet />
          </div>
        </main>
        
        {/* Onboarding Hints */}
        <OnboardingHints page={getPageName()} />
      </div>
    </TooltipProvider>
  );
};

export default ControlPanelLayout;
